import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from './api.js';
import { texts, useLang, type Lang } from './i18n.js';
import { Icon } from './shell.js';
import { Dialog, MoreOptions, toast } from './ui.js';

/**
 * Time reporting in the web app (Del 1 §1.5.21, UX-976–977): the timer pill in the top bar on every page, and one
 * dialog to start a timer or add time by hand, with the project picker (your projects first, search for the rest).
 * Time is logged per project, not per video (owner decision 2026-10-15).
 */

export interface TimeEntryView {
  id: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  client: string;
  videoId: string | null;
  videoCode: string | null;
  videoName: string | null;
  round: string | null;
  kind: TimeKind;
  workDate: string;
  startedAt: string | null;
  endedAt: string | null;
  minutes: number | null;
  note: string | null;
  source: 'timer' | 'manual' | 'monday_import';
  needsCheck: boolean;
  running: boolean;
  /** When a running timer next asks "Still working?" (every hour). */
  checkAt?: string | null;
}
interface TimerState {
  now: string;
  running: TimeEntryView | null;
  stopped?: TimeEntryView | null;
  /** The project to resume in one click when no timer runs. */
  last?: ProjectChoice | null;
  /** A timer the server just stopped by itself: no answer to the hourly check, or 12 h. */
  autoStopped?: TimeEntryView | null;
}
interface ProjectChoice {
  projectId: string;
  projectCode: string;
  projectName: string;
  client: string;
  round: string | null;
  videos: number;
  due: string | null;
  recent?: boolean;
}

export type TimeKind = 'editing' | 'shoot' | 'producer' | 'review' | 'other';
export const KIND_LABEL: Record<TimeKind, string> = { editing: 'Editing', shoot: 'Shoot', producer: 'Producing', review: 'Review', other: 'Other' };
export const KIND_LABEL_SV: Record<TimeKind, string> = { editing: 'Redigering', shoot: 'Inspelning', producer: 'Produktion', review: 'Granskning', other: 'Annat' };
export const kindLabels = (lang: Lang = 'en'): Record<TimeKind, string> => (lang === 'sv' ? KIND_LABEL_SV : KIND_LABEL);

/** The timer's own texts (pill, menu, hourly check, mini timer, Start timer / Add time dialog). */
const TX = texts(
  {
    timer: 'Timer',
    winTitle: 'KFF Hub timer',
    stillWorking: 'Still working?',
    yesContinue: 'Yes, continue',
    stop: 'Stop',
    autoStopped: (dur: string, on: string) => `Timer stopped at the last check: ${dur} on ${on}. Check the time in My time.`,
    miniNeedsChrome: 'The mini timer needs Chrome or Edge',
    miniFailed: 'Could not open the mini timer',
    switchedTo: (on: string, dur: string, from: string) => `Switched to ${on} – ${dur} logged on ${from}`,
    startedOn: (on: string) => `Timer started on ${on}`,
    startFailed: 'Could not start the timer',
    checkBody: (on: string, name: string) => `${on} · ${name} – the timer stops in 5 minutes without an answer`,
    confirmFailed: 'Could not confirm the timer',
    logged: (dur: string, on: string) => `Logged ${dur} on ${on}`,
    stoppedNothing: 'Timer stopped – under a minute, so nothing was logged',
    stopFailed: 'Could not stop the timer',
    switchTitle: (name: string, client: string) => `${name} · ${client} – switch project`,
    stopOn: (on: string) => `Stop the timer on ${on}`,
    startOnTitle: (name: string, client: string) => `Start the timer on ${name} · ${client}`,
    startOnLabel: (name: string) => `Start timer on ${name}`,
    startVerb: 'Start',
    startTimer: 'Start timer',
    startTimerTitle: 'Start the timer on a project',
    stillWorkingOn: 'Still working on',
    checkSub: 'Without an answer the timer stops at the last check in 5 minutes.',
    switchProject: 'Switch project',
    startOn: 'Start the timer on',
    switchTo: 'Switch to',
    loading: 'Loading…',
    noOtherMine: 'No other projects of yours are open.',
    noMine: 'No open projects are yours right now.',
    another: 'Another project…',
    addByHand: 'Add time by hand…',
    miniOnTop: 'Mini timer on top of other apps',
    back: 'Back',
    anotherHub: 'Another project… (opens the Hub)',
    stillThis: 'Still working on this?',
    switch: 'Switch',
    noTimer: 'No timer running',
    anotherProject: 'Another project',
    startTheTimer: 'Start the timer',
    chooseProject: 'Choose the project you work on',
    enterHowLong: 'Enter how long, for example 1:30 or 45 min (at most 16 h)',
    movedTo: (name: string, dur: string, from: string) => `Timer moved to ${name} – ${dur} logged on ${from}`,
    startedOnProject: (name: string, client: string) => `Timer started on ${name} · ${client}`,
    added: (dur: string, on: string) => `Added ${dur} on ${on}`,
    videos: (n: number) => (n === 1 ? '1 video' : `${n} videos`),
    switchTimer: 'Switch the timer',
    addTime: 'Add time',
    addDur: (dur: string) => `Add ${dur}`,
    cancel: 'Cancel',
    saving: 'Saving…',
    segLabel: 'Timer or added time',
    runningStops: (on: string) => `The timer on ${on} stops and its time is logged.`,
    project: 'Project',
    searchPh: 'Search project, client or video',
    noMatch: (q: string) => `No open project matches "${q}".`,
    noneMine: 'No open projects are yours right now – search for the one you work on.',
    yours: 'Yours',
    yourProjects: 'Your projects',
    otherProjects: 'Other open projects',
    type: 'Type',
    defaultKind: 'Default for my role',
    day: 'Day',
    howLong: 'How long',
    durHint: '1:30, 1.5 or 45 min',
    note: 'Note',
    optional: '(optional)'
  },
  {
    timer: 'Timer',
    winTitle: 'KFF Hub-timer',
    stillWorking: 'Jobbar du fortfarande?',
    yesContinue: 'Ja, fortsätt',
    stop: 'Stoppa',
    autoStopped: (dur: string, on: string) => `Timern stoppades vid senaste kontrollen: ${dur} på ${on}. Kolla tiden i Min tid.`,
    miniNeedsChrome: 'Minitimern kräver Chrome eller Edge',
    miniFailed: 'Kunde inte öppna minitimern',
    switchedTo: (on: string, dur: string, from: string) => `Bytte till ${on} – ${dur} loggat på ${from}`,
    startedOn: (on: string) => `Timer startad på ${on}`,
    startFailed: 'Kunde inte starta timern',
    checkBody: (on: string, name: string) => `${on} · ${name} – timern stoppas om 5 minuter utan svar`,
    confirmFailed: 'Kunde inte bekräfta timern',
    logged: (dur: string, on: string) => `Loggade ${dur} på ${on}`,
    stoppedNothing: 'Timern stoppad – under en minut, så inget loggades',
    stopFailed: 'Kunde inte stoppa timern',
    switchTitle: (name: string, client: string) => `${name} · ${client} – byt projekt`,
    stopOn: (on: string) => `Stoppa timern på ${on}`,
    startOnTitle: (name: string, client: string) => `Starta timern på ${name} · ${client}`,
    startOnLabel: (name: string) => `Starta timer på ${name}`,
    startVerb: 'Starta',
    startTimer: 'Starta timer',
    startTimerTitle: 'Starta timern på ett projekt',
    stillWorkingOn: 'Jobbar du fortfarande med',
    checkSub: 'Utan svar stoppas timern vid senaste kontrollen om 5 minuter.',
    switchProject: 'Byt projekt',
    startOn: 'Starta timern på',
    switchTo: 'Byt till',
    loading: 'Laddar…',
    noOtherMine: 'Du har inga andra öppna projekt.',
    noMine: 'Du har inga öppna projekt just nu.',
    another: 'Ett annat projekt…',
    addByHand: 'Lägg till tid för hand…',
    miniOnTop: 'Minitimer ovanpå andra appar',
    back: 'Tillbaka',
    anotherHub: 'Ett annat projekt… (öppnar Hubben)',
    stillThis: 'Jobbar du fortfarande med det här?',
    switch: 'Byt',
    noTimer: 'Ingen timer igång',
    anotherProject: 'Ett annat projekt',
    startTheTimer: 'Starta timern',
    chooseProject: 'Välj projektet du jobbar med',
    enterHowLong: 'Ange hur länge, till exempel 1:30 eller 45 min (högst 16 h)',
    movedTo: (name: string, dur: string, from: string) => `Timern flyttad till ${name} – ${dur} loggat på ${from}`,
    startedOnProject: (name: string, client: string) => `Timer startad på ${name} · ${client}`,
    added: (dur: string, on: string) => `La till ${dur} på ${on}`,
    videos: (n: number) => (n === 1 ? '1 video' : `${n} videor`),
    switchTimer: 'Byt projekt för timern',
    addTime: 'Lägg till tid',
    addDur: (dur: string) => `Lägg till ${dur}`,
    cancel: 'Avbryt',
    saving: 'Sparar…',
    segLabel: 'Timer eller tillagd tid',
    runningStops: (on: string) => `Timern på ${on} stoppas och tiden loggas.`,
    project: 'Projekt',
    searchPh: 'Sök projekt, kund eller video',
    noMatch: (q: string) => `Inget öppet projekt matchar ”${q}”.`,
    noneMine: 'Du har inga öppna projekt just nu – sök efter det du jobbar med.',
    yours: 'Dina',
    yourProjects: 'Dina projekt',
    otherProjects: 'Andra öppna projekt',
    type: 'Typ',
    defaultKind: 'Standard för min roll',
    day: 'Dag',
    howLong: 'Hur länge',
    durHint: '1:30, 1,5 eller 45 min',
    note: 'Anteckning',
    optional: '(valfritt)'
  }
);
type Tx = (typeof TX)['en'];

/** "1:05:09" while running; minutes as "1 h 25 min" in lists. */
export const clock = (sec: number) => {
  const s = Math.max(0, Math.floor(sec));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};
export const duration = (min: number) => {
  const hh = Math.floor(min / 60);
  const mm = min % 60;
  return hh && mm ? `${hh} h ${mm} min` : hh ? `${hh} h` : `${mm} min`;
};
/** "1:30", "1.5", "1,5", "90m", "45 min", "2h" (or Swedish "2 t", "2 tim") → minutes; null when it can't be read. */
export function parseDuration(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(',', '.');
  if (!s) return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d{1,2}):([0-5]\d)$/))) return +m[1]! * 60 + +m[2]!;
  if ((m = s.match(/^(\d+(?:\.\d+)?)\s*(?:h|t|tim|timmar)(?:\s*(\d{1,2})\s*m(?:in)?)?$/))) return Math.round(+m[1]! * 60) + (m[2] ? +m[2] : 0);
  if ((m = s.match(/^(\d+)\s*m(?:in)?$/))) return +m[1]!;
  if ((m = s.match(/^\d+(?:\.\d+)?$/))) return Math.round(+s * 60);
  return null;
}

/* ---------- a tiny bus: any page can open the dialog or tell the pill the timer changed ---------- */
export interface OpenTimer {
  mode?: 'start' | 'add';
  projectId?: string;
  workDate?: string;
}
const OPEN_EVENT = 'kff-timer-open';
const CHANGED_EVENT = 'kff-timer-changed';
export const openTimer = (o: OpenTimer = {}) => dispatchEvent(new CustomEvent<OpenTimer>(OPEN_EVENT, { detail: o }));
export const timerChanged = () => dispatchEvent(new Event(CHANGED_EVENT));
export const onTimerChanged = (fn: () => void) => {
  addEventListener(CHANGED_EVENT, fn);
  return () => removeEventListener(CHANGED_EVENT, fn);
};

/* ---------- the mini timer: a small window that stays on top of Premiere (Document Picture-in-Picture, Chrome/Edge) ---------- */
interface PipApi {
  requestWindow(o: { width: number; height: number }): Promise<Window>;
}
const pipApi = (): PipApi | null => (globalThis as { documentPictureInPicture?: PipApi }).documentPictureInPicture ?? null;
export const canPopOut = () => pipApi() !== null;

/** Opens the mini window with the Hub's styles and theme; null when the browser can't. */
async function openMiniWindow(title = 'KFF Hub timer'): Promise<Window | null> {
  const api = pipApi();
  if (!api) return null;
  const w = await api.requestWindow({ width: 320, height: 236 });
  // the Hub's own styles: built CSS files by link (so font URLs resolve), inline dev styles by copy
  for (const sheet of [...document.styleSheets]) {
    if (sheet.href) {
      const link = w.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = sheet.href;
      w.document.head.appendChild(link);
      continue;
    }
    try {
      const style = w.document.createElement('style');
      style.textContent = [...sheet.cssRules].map((r) => r.cssText).join('\n');
      w.document.head.appendChild(style);
    } catch {
      /* a sheet we may not read: the mini timer still works with the rest */
    }
  }
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme) w.document.documentElement.setAttribute('data-theme', theme);
  w.document.title = title;
  w.document.body.className = 'mini-body';
  return w;
}

/* ---------- the hourly check: "Still working on …?" in the top bar, the mini timer and as a system notification ---------- */
const CHECK_TAG = 'kff-timer-check';
const canNotify = () => typeof Notification !== 'undefined';
/** Asked when someone starts a timer (a click), so the hourly check can reach them outside the Hub. */
const askToNotify = () => {
  if (canNotify() && Notification.permission === 'default') void Notification.requestPermission().catch(() => undefined);
};
async function showCheckNotification(body: string, t: Tx = TX.en) {
  if (!canNotify() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker?.register('/timer-sw.js');
    if (reg) {
      await reg.showNotification(t.stillWorking, {
        body,
        tag: CHECK_TAG,
        requireInteraction: true,
        actions: [
          { action: 'yes', title: t.yesContinue },
          { action: 'stop', title: t.stop }
        ]
      } as NotificationOptions);
      return;
    }
  } catch {
    /* no service worker here: a plain notification still asks */
  }
  const n = new Notification(t.stillWorking, { body, tag: CHECK_TAG, requireInteraction: true });
  n.onclick = () => {
    window.focus();
    n.close();
  };
}
async function closeCheckNotification() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration('/timer-sw.js');
    for (const n of (await reg?.getNotifications({ tag: CHECK_TAG })) ?? []) n.close();
  } catch {
    /* nothing to close */
  }
}

/** How often the top-bar timer re-reads its state while visible (a change in another tab or device shows by then). */
const POLL_MS = 5 * 60_000;

const todayStockholm = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
/** What a timer runs on, as people say it: the video or project name (the code stays in the data and in tooltips). */
const what = (e: Pick<TimeEntryView, 'videoName' | 'projectName' | 'round'>) => [e.videoName ?? e.projectName, e.round].filter(Boolean).join(' · ');

/**
 * The top-bar timer, built for as few clicks as possible:
 * - idle: "▶ FBK-2610-01" resumes the project you last logged time on in one click; the arrow opens your projects;
 * - running: "● FBK-2610-01 · Draft 1 · 1:24:05" with Stop (one click); the label opens your projects to switch (two clicks);
 * - a start from a project row (openTimer with a projectId) starts straight away, no dialog.
 * The dialog is only for searching other projects and for adding time by hand.
 */
export function TimerPill(p: { api: Api }) {
  const [state, setState] = useState<TimerState | null>(null);
  const [skew, setSkew] = useState(0);
  const [, tick] = useState(0);
  const [dialog, setDialog] = useState<OpenTimer | null>(null);
  const [menu, setMenu] = useState(false);
  const [mine, setMine] = useState<ProjectChoice[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [mini, setMini] = useState<Window | null>(null);
  const [miniList, setMiniList] = useState(false);
  const notified = useRef<string | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const t = TX[useLang()];
  // the effect below keeps its first callbacks (poll, open event, notification answers): they read the texts here
  const tr = useRef(t);
  tr.current = t;
  const load = () =>
    p.api.get<TimerState>('/api/time/timer').then(
      (s) => {
        setSkew(Date.parse(s.now) - Date.now());
        setState(s);
        if (s.autoStopped) {
          void closeCheckNotification();
          toast({ text: tr.current.autoStopped(duration(s.autoStopped.minutes ?? 0), what(s.autoStopped)) });
        }
      },
      () => setState({ now: new Date().toISOString(), running: null })
    );
  const set = (s: TimerState) => {
    setSkew(Date.parse(s.now) - Date.now());
    setState(s);
    timerChanged();
  };
  const popOut = async () => {
    setMenu(false);
    try {
      const w = await openMiniWindow(tr.current.winTitle);
      if (!w) return toast({ text: tr.current.miniNeedsChrome });
      w.addEventListener('pagehide', () => setMini(null));
      setMiniList(false);
      setMini(w);
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : tr.current.miniFailed });
    }
  };
  // the mini window closes with the Hub tab; close it ourselves when the pill goes away (sign-out)
  useEffect(() => () => mini?.close(), [mini]);
  useEffect(() => {
    if (!mini || !miniList || mine !== null) return;
    p.api.get<{ mine: ProjectChoice[] }>('/api/time/projects').then(
      (r) => setMine(r.mine),
      () => setMine([])
    );
  }, [mini, miniList]);
  const start = async (projectId: string, code?: string) => {
    askToNotify();
    setMenu(false);
    setMiniList(false);
    setBusy(true);
    try {
      const s = await p.api.post<TimerState>('/api/time/timer/start', { projectId });
      set(s);
      const on = s.running ? what(s.running) : code;
      toast({ text: s.stopped ? tr.current.switchedTo(on ?? '', duration(s.stopped.minutes ?? 0), what(s.stopped)) : tr.current.startedOn(on ?? '') });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : tr.current.startFailed });
    } finally {
      setBusy(false);
    }
  };
  // the mini window can be on screen while the Hub tab is hidden
  const miniOpen = useRef(false);
  miniOpen.current = !!mini;
  useEffect(() => {
    void load();
    // every check reads the database (Hyperdrive bills each query), so only while someone can see the timer, and
    // straight away when the tab comes back; the clock and the hourly check run locally between reads
    const poll = setInterval(() => (document.visibilityState === 'visible' || miniOpen.current) && void load(), POLL_MS);
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    const onOpen = (e: Event) => {
      const o = (e as CustomEvent<OpenTimer>).detail ?? {};
      // a start on a given project needs nothing more from the person
      if (o.mode === 'start' && o.projectId) void start(o.projectId);
      else setDialog(o);
    };
    addEventListener(OPEN_EVENT, onOpen);
    const off = onTimerChanged(() => void load());
    // an answer given in the system notification
    const onAnswer = (e: MessageEvent) => {
      const m = e.data as { type?: string; action?: string } | null;
      if (m?.type !== 'kff-timer-check') return;
      if (m.action === 'yes') void confirm();
      else if (m.action === 'stop') void stop();
    };
    navigator.serviceWorker?.addEventListener('message', onAnswer);
    return () => {
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      removeEventListener(OPEN_EVENT, onOpen);
      navigator.serviceWorker?.removeEventListener('message', onAnswer);
      off();
    };
  }, [p.api]);
  useEffect(() => {
    if (!menu) return;
    setMine(null);
    p.api.get<{ mine: ProjectChoice[] }>('/api/time/projects').then(
      (r) => setMine(r.mine),
      () => setMine([])
    );
    const onDown = (e: Event) => !wrap.current?.contains(e.target as Node) && setMenu(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(false);
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [menu]);
  const run = state?.running ?? null;
  const last = run ? null : (state?.last ?? null);
  useEffect(() => {
    if (!run) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [run?.id]);
  const elapsed = run?.startedAt ? (Date.now() + skew - Date.parse(run.startedAt)) / 1000 : 0;
  const checkDue = !!run?.checkAt && Date.now() + skew >= Date.parse(run.checkAt);
  useEffect(() => {
    if (!checkDue || !run?.checkAt || notified.current === run.checkAt) return;
    notified.current = run.checkAt;
    void showCheckNotification(tr.current.checkBody(what(run), run.projectName), tr.current);
  }, [checkDue, run?.checkAt]);
  const confirm = async () => {
    try {
      set(await p.api.post<TimerState>('/api/time/timer/confirm'));
      void closeCheckNotification();
    } catch (e) {
      // the timer may already have stopped on its own: show what the server has
      void load();
      toast({ text: e instanceof Error ? e.message : tr.current.confirmFailed });
    }
  };
  // the tab shows the clock too, so a Hub tab in the background still says a timer runs
  useEffect(() => {
    const base = document.title.replace(/^● [\d:]+ ·\s?/, '');
    document.title = run ? `● ${clock(elapsed).replace(/:\d\d$/, '')} · ${base}` : base;
  });
  const stop = async () => {
    setMenu(false);
    setBusy(true);
    try {
      const r = await p.api.post<TimerState>('/api/time/timer/stop');
      set(r);
      void closeCheckNotification();
      toast({ text: r.stopped ? tr.current.logged(duration(r.stopped.minutes ?? 0), what(r.stopped)) : tr.current.stoppedNothing });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : tr.current.stopFailed });
    } finally {
      setBusy(false);
    }
  };
  const choices = (mine ?? []).filter((c) => c.projectId !== run?.projectId).slice(0, 8);
  return (
    <div class="menu-wrap" ref={wrap}>
      {run ? (
        <span class={`timer-pill${checkDue ? ' is-check' : ''}`} role="group" aria-label={t.timer} data-tour="timer">
          <button type="button" class="timer-what" title={t.switchTitle(run.projectName, run.client)} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            <span class="timer-icon is-running">
              <Icon name="stopwatch" />
            </span>
            <span class="timer-label">{what(run)}</span>
            <span class="timer-clock" aria-live="off">
              {clock(elapsed)}
            </span>
          </button>
          <button type="button" class="timer-stop" aria-label={t.stopOn(what(run))} disabled={busy} onClick={stop}>
            <Icon name="stop" />
            <span class="timer-stop-text" aria-hidden="true">
              {t.stop}
            </span>
          </button>
        </span>
      ) : (
        <span class="timer-pill timer-pill--idle" role="group" aria-label={t.timer} data-tour="timer">
          {last ? (
            <button type="button" class="timer-what timer-resume" title={t.startOnTitle(last.projectName, last.client)} aria-label={t.startOnLabel(last.projectName)} disabled={busy} onClick={() => start(last.projectId, last.projectName)}>
              <span class="timer-icon">
                <Icon name="stopwatch" />
              </span>
              <span class="timer-label">
                <span class="timer-verb">{t.startVerb}</span> {last.projectName}
              </span>
            </button>
          ) : null}
          <button type="button" class={last ? 'timer-more' : 'timer-what'} aria-label={t.startTimer} title={t.startTimerTitle} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu(!menu)}>
            {last ? (
              <Icon name="chevron-down" />
            ) : (
              <>
                <span class="timer-icon">
                  <Icon name="stopwatch" />
                </span>
                <span class="timer-label timer-start-text">{t.startTimer}</span>
              </>
            )}
          </button>
        </span>
      )}
      {checkDue && run && !menu ? (
        <div class="menu timer-check" role="alertdialog" aria-labelledby="timer-check-q">
          <p class="timer-check-q" id="timer-check-q">
            {t.stillWorkingOn} <strong>{what(run)}</strong>?
          </p>
          <p class="small muted timer-check-sub">{t.checkSub}</p>
          <div class="timer-check-actions">
            <button type="button" class="btn btn-primary" onClick={confirm}>
              {t.yesContinue}
            </button>
            <button type="button" class="btn" disabled={busy} onClick={stop}>
              {t.stop}
            </button>
          </div>
        </div>
      ) : null}
      {menu ? (
        <div class="menu timer-menu" role="menu" aria-label={run ? t.switchProject : t.startOn}>
          <div class="menu-label">{run ? t.switchTo : t.startOn}</div>
          {mine === null ? (
            <p class="small muted timer-menu-note">{t.loading}</p>
          ) : !choices.length ? (
            <p class="small muted timer-menu-note">{run ? t.noOtherMine : t.noMine}</p>
          ) : (
            choices.map((c) => (
              <button type="button" role="menuitem" class="menu-item timer-choice" title={c.projectCode} disabled={busy} onClick={() => start(c.projectId, c.projectName)}>
                <Icon name="play" />
                <span class="tp-main">
                  <span class="tp-name">{c.projectName}</span>
                  <span class="tp-sub">
                    {c.client}
                    {c.round ? ` · ${c.round}` : ''}
                  </span>
                </span>
              </button>
            ))
          )}
          <div class="menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            class="menu-item"
            onClick={() => {
              setMenu(false);
              setDialog({ mode: 'start' });
            }}
          >
            <Icon name="search" />
            {t.another}
          </button>
          <button
            type="button"
            role="menuitem"
            class="menu-item"
            onClick={() => {
              setMenu(false);
              setDialog({ mode: 'add' });
            }}
          >
            <Icon name="plus" />
            {t.addByHand}
          </button>
          {canPopOut() && !mini ? (
            <>
              <div class="menu-sep" role="separator" />
              <button type="button" role="menuitem" class="menu-item" onClick={popOut}>
                <Icon name="pop-out" />
                {t.miniOnTop}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {mini
        ? createPortal(
            <MiniTimer
              t={t}
              run={run}
              last={last}
              elapsed={elapsed}
              busy={busy}
              checkDue={checkDue}
              onConfirm={confirm}
              list={miniList}
              mine={mine}
              onList={setMiniList}
              onStart={start}
              onStop={stop}
              onOpenHub={() => {
                setMiniList(false);
                setDialog({ mode: 'start' });
                window.focus();
              }}
            />,
            mini.document.body
          )
        : null}
      {dialog ? <TimerDialog api={p.api} open={dialog} running={run} onClose={() => setDialog(null)} onStarted={set} /> : null}
    </div>
  );
}

/** The mini timer's content: the same timer in a small always-on-top window, made for a glance and one click. */
function MiniTimer(p: {
  t: Tx;
  run: TimeEntryView | null;
  last: ProjectChoice | null;
  elapsed: number;
  busy: boolean;
  checkDue: boolean;
  onConfirm: () => void;
  list: boolean;
  mine: ProjectChoice[] | null;
  onList: (on: boolean) => void;
  onStart: (projectId: string, code?: string) => void;
  onStop: () => void;
  onOpenHub: () => void;
}) {
  const t = p.t;
  if (p.list) {
    const rows = (p.mine ?? []).filter((c) => c.projectId !== p.run?.projectId).slice(0, 6);
    return (
      <div class="mini mini--list">
        <div class="mini-head">
          <span class="menu-label">{p.run ? t.switchTo : t.startOn}</span>
          <button type="button" class="mini-link" onClick={() => p.onList(false)}>
            {t.back}
          </button>
        </div>
        {p.mine === null ? <p class="small muted">{t.loading}</p> : null}
        {rows.map((c) => (
          <button type="button" class="mini-choice" title={c.projectCode} disabled={p.busy} onClick={() => p.onStart(c.projectId, c.projectName)}>
            <span class="tp-name">{c.projectName}</span>
            <span class="tp-sub">{c.client}</span>
          </button>
        ))}
        <button type="button" class="mini-link" onClick={p.onOpenHub}>
          {t.anotherHub}
        </button>
      </div>
    );
  }
  if (p.run && p.checkDue)
    return (
      <div class="mini is-check" role="alertdialog" aria-labelledby="mini-check-q">
        <div class="mini-what">
          <span class="timer-icon is-running">
            <Icon name="stopwatch" />
          </span>
          <span class="mini-code">{what(p.run)}</span>
        </div>
        <div class="mini-name" id="mini-check-q">
          {t.stillThis}
        </div>
        <div class="small mini-sub">{t.checkSub}</div>
        <div class="mini-actions">
          <button type="button" class="btn btn-primary mini-btn" onClick={p.onConfirm}>
            {t.yesContinue}
          </button>
          <button type="button" class="btn mini-btn" disabled={p.busy} onClick={p.onStop}>
            {t.stop}
          </button>
        </div>
      </div>
    );
  if (p.run)
    return (
      <div class="mini is-running">
        <div class="mini-what">
          <span class="timer-icon is-running">
            <Icon name="stopwatch" />
          </span>
          <span class="mini-code">{what(p.run)}</span>
        </div>
        <div class="mini-name">{p.run.projectName}</div>
        <div class="mini-clock" aria-live="off">
          {clock(p.elapsed)}
        </div>
        <div class="mini-actions">
          <button type="button" class="btn btn-primary mini-btn" disabled={p.busy} onClick={p.onStop}>
            <Icon name="stop" />
            {t.stop}
          </button>
          <button type="button" class="btn mini-btn" onClick={() => p.onList(true)}>
            {t.switch}
          </button>
        </div>
      </div>
    );
  return (
    <div class="mini">
      <div class="mini-what">
        <span class="timer-icon">
          <Icon name="stopwatch" />
        </span>
        <span class="mini-code">{t.noTimer}</span>
      </div>
      {p.last ? (
        <button type="button" class="btn btn-primary mini-btn mini-resume" disabled={p.busy} onClick={() => p.onStart(p.last!.projectId, p.last!.projectName)}>
          <Icon name="play" />
          <span>
            {t.startVerb} {p.last.projectName}
            <span class="mini-sub">{p.last.client}</span>
          </span>
        </button>
      ) : null}
      <button type="button" class="btn mini-btn" onClick={() => p.onList(true)}>
        {p.last ? t.anotherProject : t.startTheTimer}
      </button>
    </div>
  );
}

/** Start timer / Add time: pick the project (yours first), the kind, and for added time the day and how long. */
export function TimerDialog(p: { api: Api; open: OpenTimer; running: TimeEntryView | null; onClose: () => void; onStarted: (s: TimerState) => void }) {
  const [mode, setMode] = useState<'start' | 'add'>(p.open.mode ?? 'start');
  const [q, setQ] = useState('');
  const [choices, setChoices] = useState<{ mine: ProjectChoice[]; others: ProjectChoice[] } | null>(null);
  const [pick, setPick] = useState<ProjectChoice | null>(null);
  const [kind, setKind] = useState<TimeKind | ''>('');
  const [date, setDate] = useState(p.open.workDate ?? todayStockholm());
  const [dur, setDur] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const lang = useLang();
  const t = TX[lang];
  const seq = useRef(0);
  useEffect(() => {
    const n = ++seq.current;
    const fetchNow = () =>
      p.api.get<{ mine: ProjectChoice[]; others: ProjectChoice[] }>(`/api/time/projects${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`).then(
        (r) => {
          if (n !== seq.current) return;
          setChoices(r);
          if (pick) return;
          // the project asked for, else the one you logged time on last (not the one already running)
          if (p.open.projectId) setPick([...r.mine, ...r.others].find((c) => c.projectId === p.open.projectId) ?? null);
          else if (!q) setPick(r.mine.find((c) => c.recent && c.projectId !== p.running?.projectId) ?? null);
        },
        (e) => n === seq.current && setErr(e instanceof Error ? e.message : String(e))
      );
    if (!q) {
      void fetchNow();
      return;
    }
    const t = setTimeout(fetchNow, 250);
    return () => clearTimeout(t);
  }, [q]);
  const minutes = parseDuration(dur);
  const submit = async () => {
    setErr('');
    if (!pick) return setErr(t.chooseProject);
    if (mode === 'add' && (minutes === null || minutes < 1 || minutes > 960)) return setErr(t.enterHowLong);
    setBusy(true);
    try {
      const body = { projectId: pick.projectId, ...(kind ? { kind } : {}) };
      if (mode === 'start') {
        const s = await p.api.post<TimerState>('/api/time/timer/start', body);
        p.onStarted(s);
        toast({ text: s.stopped ? t.movedTo(pick.projectName, duration(s.stopped.minutes ?? 0), what(s.stopped)) : t.startedOnProject(pick.projectName, pick.client) });
      } else {
        const e = await p.api.post<TimeEntryView>('/api/time/entries', { ...body, workDate: date, minutes, note: note.trim() || null });
        timerChanged();
        toast({
          text: t.added(duration(e.minutes ?? 0), what(e)),
          undo: async () => {
            await p.api.del(`/api/time/entries/${e.id}`);
            timerChanged();
          }
        });
      }
      p.onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const list = (title: string, rows: ProjectChoice[]) =>
    rows.length ? (
      <fieldset class="tp-group">
        <legend class="label">{title}</legend>
        {rows.map((c) => (
          <label class={`tp-option${pick?.projectId === c.projectId ? ' is-on' : ''}`} title={c.projectCode}>
            <input type="radio" name="tp-project" checked={pick?.projectId === c.projectId} onChange={() => setPick(c)} />
            <span class="tp-main">
              <span class="tp-name">{c.projectName}</span>
              <span class="tp-sub">
                {c.client} · {t.videos(c.videos)}
              </span>
            </span>
            {c.round ? <span class="pill pill--neutral">{c.round}</span> : null}
          </label>
        ))}
      </fieldset>
    ) : null;
  return (
    <Dialog
      title={mode === 'start' ? (p.running ? t.switchTimer : t.startTimer) : t.addTime}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? t.saving : mode === 'start' ? (p.running ? t.switch : t.startTimer) : minutes ? t.addDur(duration(minutes)) : t.addTime}
          </button>
        </>
      }
    >
      <div class="seg" role="group" aria-label={t.segLabel}>
        <button type="button" class={`seg-btn${mode === 'start' ? ' is-on' : ''}`} aria-pressed={mode === 'start'} onClick={() => setMode('start')}>
          {t.timer}
        </button>
        <button type="button" class={`seg-btn${mode === 'add' ? ' is-on' : ''}`} aria-pressed={mode === 'add'} onClick={() => setMode('add')}>
          {t.addTime}
        </button>
      </div>
      {p.running && mode === 'start' ? <p class="small muted">{t.runningStops(what(p.running))}</p> : null}
      <div class="field">
        <label class="label" for="tp-q">
          {t.project}
        </label>
        <input class="input input--wide" id="tp-q" type="search" placeholder={t.searchPh} value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} />
      </div>
      <div class="tp-list">
        {choices === null ? (
          <p class="small muted">{t.loading}</p>
        ) : !choices.mine.length && !choices.others.length ? (
          <p class="small muted">{q ? t.noMatch(q) : t.noneMine}</p>
        ) : (
          <>
            {list(q ? t.yours : t.yourProjects, choices.mine)}
            {list(t.otherProjects, choices.others)}
          </>
        )}
      </div>
      {mode === 'add' ? (
        <div class="field">
          <label class="label" for="tp-dur">
            {t.howLong}
          </label>
          <input class="input tp-dur" id="tp-dur" inputMode="decimal" placeholder="1:30" value={dur} onInput={(e) => setDur((e.target as HTMLInputElement).value)} aria-describedby="tp-dur-hint" />
          <span class="small muted" id="tp-dur-hint">
            {dur && minutes ? duration(minutes) : t.durHint}
          </span>
        </div>
      ) : null}
      <MoreOptions id={`timer-${mode}`} open={mode === 'add' && Boolean(p.open.workDate) && p.open.workDate !== todayStockholm()} filled={(kind ? 1 : 0) + (mode === 'add' ? (date !== todayStockholm() ? 1 : 0) + (note.trim() ? 1 : 0) : 0)}>
        <div class="tp-row">
          <div class="field">
            <label class="label" for="tp-kind">
              {t.type}
            </label>
            <select class="input tp-kind" id="tp-kind" value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value as TimeKind | '')}>
              <option value="">{t.defaultKind}</option>
              {(Object.keys(KIND_LABEL) as TimeKind[]).map((k) => (
                <option value={k}>{kindLabels(lang)[k]}</option>
              ))}
            </select>
          </div>
          {mode === 'add' ? (
            <div class="field">
              <label class="label" for="tp-date">
                {t.day}
              </label>
              <input class="input input--date" type="date" id="tp-date" value={date} max={todayStockholm()} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
            </div>
          ) : null}
        </div>
        {mode === 'add' ? (
          <div class="field">
            <label class="label" for="tp-note">
              {t.note} <span class="muted">{t.optional}</span>
            </label>
            <input class="input input--wide" id="tp-note" maxLength={500} value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
          </div>
        ) : null}
      </MoreOptions>
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </Dialog>
  );
}
